
// Storage Invoice Itemized Utilities
// Extracted from StorageInvoiceDetailsItemized.js

import { Tooltip } from "@material-ui/core";

/**
 * Returns the cell value for a summary row in the invoice itemized table.
 * @param {object} summary - The summary row object
 * @param {object} col - The column definition
 * @param {boolean} isBundle - Whether this row is a bundle
 * @param {object} options - Additional options (classes, tooltip text)
 */
export function getInvoiceSummaryCellValue(summary, col, isBundle, options = {}) {
    const { classes, pendingTooltip } = options;
    if (col?.key === 'activityDate' && isBundle) {
        return '';
    }
    if (col?.key === 'totalCharge') {
        if (isBundle && summary?.isBundlePending) {
            return (
                <Tooltip title={pendingTooltip || 'Bundle is not complete yet, so no charge is applied yet.'}>
                    <span className={classes?.pendingCharge}>Not charged yet</span>
                </Tooltip>
            );
        }
        return col.component ? col.component({ ...summary, totalCharge: summary?.totalCharge ?? 0 }) : summary?.totalCharge ?? 0;
    }
    if (col?.key === 'activityName' && isBundle) {
        return summary?.bundleName ?? summary?.activityBundleName ?? summary?.activityName ?? '';
    }
    return col.component ? col.component(summary) : summary?.[col.key] ?? 0;
}

/**
 * Determines if the given activity or bundleActivities represent a bundle service.
 */
export const isBundleService = (activity, bundleActivities) => {
    if (activity?.isBundle) {
        return true;
    }
    if (activity?.bundleId) {
        return true;
    }
    return bundleActivities.length > 0;
};

/**
 * Returns the cell value for a bundle activity row.
 */
export const getBundleActivityCellValue = (bundleActivity, col, hidePrice = false) => {
    if (col?.key === 'vin') {
        return '';
    }
    if (col?.key === 'assetId') {
        return '';
    }
    if (col?.key === 'activityName') {
        return bundleActivity?.activityName ?? bundleActivity?.name ?? bundleActivity?.description ?? '';
    }
    if (col?.key === 'activityDate') {
        return col.component ? col.component(bundleActivity) : bundleActivity?.activityDate ?? '';
    }
    if (col?.key === 'totalCharge') {
        if (hidePrice) {
            return '';
        }
        const amount = bundleActivity?.totalCharge ?? bundleActivity?.charge ?? bundleActivity?.price ?? 0;
        return col.component ? col.component({ ...bundleActivity, totalCharge: amount }) : amount;
    }
    return '';
};

/**
 * Returns the cell value for a bundle discount row.
 */
export const getBundleDiscountCellValue = (discount, col) => {
    if (col?.key === 'activityName') {
        return 'Bundle Discount';
    }
    if (col?.key === 'totalCharge') {
        const amount = discount > 0 ? -discount : discount;
        return col.component ? col.component({ totalCharge: amount }) : amount;
    }
    return '';
};

/**
 * Groups activities into bundle rows from a flat response.
 */
export const getBundleRowsFromFlatResponse = (activities = []) => {
    const groupedBundles = new Map();
    const result = [];

    activities.forEach((activity) => {
        const bundleId = activity?.activityLogBundleId;
        if (!bundleId) {
            result.push({
                summary: activity,
                bundleActivities: [],
            });
            return;
        }

        if (!groupedBundles.has(bundleId)) {
            groupedBundles.set(bundleId, []);
        }
        groupedBundles.get(bundleId).push(activity);
    });

    groupedBundles.forEach((rows) => {
        const bundleActivities = rows;
        const summarySource = rows[0];
        const isBundlePending = rows.some((row) => !!row?.isBundlePending);

        const activitiesChargeSum = bundleActivities.reduce((sum, row) => sum + (Number(row?.totalCharge) || 0), 0);
        const resolvedBundleTotalCharge = summarySource.bundleTotalCharge;

        const totalCharge = isBundlePending
            ? 0
            : resolvedBundleTotalCharge;

        // Discount gap = bundle total - sum of grouped bundle activity lines.
        const calculatedGap = resolvedBundleTotalCharge - activitiesChargeSum;
        const bundleDiscount = calculatedGap;

        result.push({
            summary: {
                ...summarySource,
                isBundle: true,
                isBundlePending,
                activityName: summarySource.bundleName ?? '',
                totalCharge,
            },
            bundleActivities,
            bundleDiscount,
        });
    });

    return result;
};

/**
 * Normalizes activities into mechanical rows, handling bundles if present.
 */
export const normalizeMechanicalRows = (activities = []) => {
    const hasFlatBundleShape = activities.some((activity) => activity?.activityLogBundleId);

    if (hasFlatBundleShape) {
        return getBundleRowsFromFlatResponse(activities);
    }

    return activities.map((activity) => ({
        summary: activity,
        bundleActivities: [],
    }));
};
